import { useState } from 'react';
import { MessageSquarePlus, Bug, Lightbulb, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useFeedback } from '../../hooks/useFeedback';
import { FeedbackType } from '../../types/feedback';

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<FeedbackType>('bug');
  const [message, setMessage] = useState('');
  const [includeLogs, setIncludeLogs] = useState(true);
  const { submitFeedback, isSubmitting } = useFeedback();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    const success = await submitFeedback(type, message, includeLogs);
    if (success) {
      setOpen(false);
      setMessage('');
      setType('bug');
      setIncludeLogs(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="fixed bottom-4 right-4 z-[9999] h-12 w-12 rounded-full shadow-lg"
          size="icon"
          title="Enviar Feedback"
        >
          <MessageSquarePlus className="h-6 w-6" />
          <span className="sr-only">Enviar Feedback</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Enviar Feedback</DialogTitle>
          <DialogDescription>
            Ajude-nos a melhorar reportando erros ou sugerindo melhorias.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">Tipo</Label>
            <Select
              value={type}
              onValueChange={(value) => setType(value as FeedbackType)}
            >
              <SelectTrigger id="type">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">
                  <div className="flex items-center gap-2">
                    <Bug className="h-4 w-4" /> Relatar Erro
                  </div>
                </SelectItem>
                <SelectItem value="suggestion">
                  <div className="flex items-center gap-2">
                    <Lightbulb className="h-4 w-4" /> Sugestão
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="message">Mensagem</Label>
            <Textarea
              id="message"
              placeholder="Descreva o que aconteceu ou sua ideia..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[100px]"
              required
            />
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="include-logs"
              checked={includeLogs}
              onCheckedChange={(checked) => setIncludeLogs(checked as boolean)}
            />
            <Label htmlFor="include-logs" className="text-sm font-normal">
              Incluir logs do sistema e contexto (recomendado)
            </Label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={isSubmitting || !message.trim()}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Enviar Feedback
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
